import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  RISKFLOW_SORT_MODES,
  type RiskFlowSortMode,
} from "../../lib/riskflow-source-filter";
import { DropdownSurface } from "./DropdownSurface";

interface RiskFlowSortMenuProps {
  selected: RiskFlowSortMode;
  onSelect: (mode: RiskFlowSortMode) => void;
}

export function RiskFlowSortMenu({
  selected,
  onSelect,
}: RiskFlowSortMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const label =
    RISKFLOW_SORT_MODES.find((mode) => mode.value === selected)?.label ??
    "Latest";

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !surfaceRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 rounded border bg-[var(--fintheon-bg)] px-1.5 py-0.5 text-[10px] transition-colors focus:outline-none ${
          open || selected !== "latest"
            ? "border-[var(--fintheon-accent)]/40 text-[var(--fintheon-accent)]"
            : "border-zinc-800 text-zinc-400 hover:text-zinc-300"
        }`}
        title="Sort RiskFlow"
      >
        <span>Sort: {label}</span>
        <ChevronDown
          className={`h-2.5 w-2.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <DropdownSurface
        open={open}
        anchorRef={buttonRef}
        surfaceRef={surfaceRef}
        align="right"
        minWidth={148}
      >
        {RISKFLOW_SORT_MODES.map((mode) => {
          const isOn = mode.value === selected;
          return (
            <button
              key={mode.value}
              type="button"
              onClick={() => {
                onSelect(mode.value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-[10px] transition-colors ${
                isOn
                  ? "bg-[var(--fintheon-accent)]/5 text-[var(--fintheon-accent)]"
                  : "text-[var(--fintheon-text)]/52 hover:bg-[var(--fintheon-accent)]/[0.075] hover:text-[var(--fintheon-text)]/82"
              }`}
            >
              <span className="inline-flex h-3 w-3 items-center justify-center">
                {isOn && (
                  <Check className="h-2.5 w-2.5 text-[var(--fintheon-accent)]" />
                )}
              </span>
              <span className="flex-1 text-left tracking-wider">
                {mode.label}
              </span>
            </button>
          );
        })}
      </DropdownSurface>
    </div>
  );
}
